import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import apiClient, { getApiUrl } from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatIndiaTime, isPastIndiaTime } from '../config/india-timezone';
import cronParser from 'cron-parser';
import {
    Play,
    Pause,
    Plus,
    Edit,
    Trash2,
    Mail,
    BarChart3,
    Calendar,
    X,
    Clock,
    Search,
    Filter,
    ChevronLeft,
    ChevronRight,
    CheckSquare,
    Square,
    Loader2
} from 'lucide-react';
import { EmailCampaignsSkeleton } from '../components/Skeleton';

const EmailCampaigns = () => {
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const queryClient = useQueryClient();

    const [flowLogs, setFlowLogs] = useState(null);
    const [showFlowLogs, setShowFlowLogs] = useState(false);
    const [logsLoading, setLogsLoading] = useState(false);
    const [selectedFlowId, setSelectedFlowId] = useState(null);
    const [logsPage, setLogsPage] = useState(1);
    const [logsPerPage, setLogsPerPage] = useState(20);
    const [deletingLogs, setDeletingLogs] = useState(false);
    const [logsFilter, setLogsFilter] = useState('all'); // all, success, error, warning
    const [currentPage, setCurrentPage] = useState(1);
    const limit = 20;
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedFlows, setSelectedFlows] = useState([]);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [stoppingAll, setStoppingAll] = useState(false);
    const [bulkStopping, setBulkStopping] = useState(false);
    const [countdowns, setCountdowns] = useState({});
    const [deletingCampaigns, setDeletingCampaigns] = useState(new Set());
    const [togglingCampaigns, setTogglingCampaigns] = useState(new Set());
    const [campaignProgress, setCampaignProgress] = useState({});

    // Fetch campaigns using React Query
    const { data: flowsData, isLoading: loading } = useQuery(
        ['emailCampaigns', currentWorkspace?.id, currentPage, searchTerm, statusFilter],
        async () => {
            if (!currentWorkspace?.id) {
                return { success: true, flows: [], pagination: { page: 1, limit: 20, total: 0, pages: 0 } };
            }

            const headers = { 'x-workspace-id': currentWorkspace.id };
            const params = {
                page: currentPage,
                limit: limit,
                ...(searchTerm && { search: searchTerm }),
                ...(statusFilter && { status: statusFilter })
            };

            const response = await apiClient.get('/api/email-campaigns', { headers, params });
            return response.data;
        },
        {
            enabled: !!currentWorkspace?.id,
            onError: (error) => {
                console.error('Error fetching campaigns:', error);
                toast.error('Failed to fetch campaigns');
            }
        }
    );

    const flows = flowsData?.flows || [];
    const pagination = flowsData?.pagination || { page: 1, limit: 20, total: 0, pages: 0 };

    // Live campaign send progress via SSE (not Socket.io)
    useEffect(() => {
        if (!currentWorkspace?.id) {
            return undefined;
        }

        const es = new EventSource(`${getApiUrl()}/api/realtime/events`);

        const handleCampaignProgress = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (Number(data.workspaceId) !== Number(currentWorkspace.id)) {
                    return;
                }

                setCampaignProgress((prev) => ({
                    ...prev,
                    [data.campaignId]: data,
                }));

                if (data.status === 'completed' || data.status === 'failed') {
                    queryClient.invalidateQueries(['emailCampaigns', currentWorkspace.id]);

                    setTimeout(() => {
                        setCampaignProgress((prev) => {
                            const next = { ...prev };
                            delete next[data.campaignId];
                            return next;
                        });
                    }, 10000);
                }
            } catch (error) {
                console.error('Error parsing campaign progress event:', error);
            }
        };

        es.addEventListener('campaign-progress', handleCampaignProgress);

        return () => {
            es.removeEventListener('campaign-progress', handleCampaignProgress);
            es.close();
        };
    }, [currentWorkspace?.id, queryClient]);

    // Calculate next execution time for a flow
    const calculateNextExecution = useCallback((flow) => {
        if (!flow.schedule_config || flow.status !== 'active') {
            return null;
        }

        let scheduleConfig = flow.schedule_config;
        if (typeof scheduleConfig === 'string') {
            try {
                scheduleConfig = JSON.parse(scheduleConfig);
            } catch (error) {
                console.error('Error parsing schedule_config JSON:', error);
                return null;
            }
        }

        const { schedule_type, schedule_time, cron_expr } = scheduleConfig;
        const now = new Date();

        if (schedule_type === 'once') {
            const scheduledTime = new Date(schedule_time);
            return scheduledTime > now ? scheduledTime : null;
        } else if ((schedule_type === 'cron' || schedule_type === 'recurring') && cron_expr) {
            try {
                const interval = cronParser.parseExpression(cron_expr);
                return interval.next().toDate();
            } catch (error) {
                console.error('Error parsing cron expression:', error);
                return null;
            }
        }

        return null;
    }, []);

    // Countdown timer effect
    useEffect(() => {
        if (flows.length === 0) return;

        const updateCountdowns = () => {
            const newCountdowns = {};
            flows.forEach(flow => {
                if (flow.status === 'active' && flow.trigger_type === 'scheduled' && flow.schedule_config) {
                    const nextExecution = calculateNextExecution(flow);
                    if (nextExecution) {
                        const now = new Date();
                        const timeDiff = nextExecution.getTime() - now.getTime();
                        if (timeDiff > 0) {
                            const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                            const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
                            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
                            newCountdowns[flow.id] = { days, hours, minutes, seconds, nextExecution };
                        }
                    }
                }
            });
            setCountdowns(newCountdowns);
        };

        updateCountdowns();
        const interval = setInterval(updateCountdowns, 1000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(flows)]);

    // Fetch flow logs
    const fetchFlowLogs = async (flowId, page = 1, filter = logsFilter) => {
        try {
            setLogsLoading(true);
            
            // Reset filter to 'all' if opening a different campaign
            if (flowId !== selectedFlowId) {
                setLogsFilter('all');
                filter = 'all';
            }
            
            setSelectedFlowId(flowId);
            setLogsPage(page);
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const params = new URLSearchParams({ page, limit: logsPerPage });
            if (filter && filter !== 'all') {
                params.append('log_level', filter);
            }
            const response = await apiClient.get(`/api/email-campaigns/${flowId}/logs?${params.toString()}`, { headers });
            setFlowLogs(response.data);
            setShowFlowLogs(true);
        } catch (error) {
            console.error('Error fetching campaign logs:', error);
            toast.error('Failed to fetch campaign logs');
        } finally {
            setLogsLoading(false);
        }
    };

    // Delete specific log
    const deleteLog = async (logId) => {
        if (!window.confirm('Are you sure you want to delete this log?')) {
            return;
        }

        try {
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            await apiClient.delete(`/api/email-campaigns/logs/${logId}`, { headers });
            toast.success('Log deleted successfully');
            fetchFlowLogs(selectedFlowId, logsPage, logsFilter);
        } catch (error) {
            console.error('Error deleting log:', error);
            toast.error('Failed to delete log');
        }
    };

    // Handle log filter change
    const handleLogsFilterChange = (filter) => {
        setLogsFilter(filter);
        fetchFlowLogs(selectedFlowId, 1, filter);
    };

    // Delete all logs for current flow
    const deleteAllFlowLogs = async () => {
        if (!window.confirm(`Are you sure you want to delete ALL logs for this campaign? This action cannot be undone.`)) {
            return;
        }

        try {
            setDeletingLogs(true);
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const response = await apiClient.delete(`/api/email-campaigns/${selectedFlowId}/logs`, { headers });
            toast.success(`Deleted ${response.data.deletedCount} logs successfully`);
            fetchFlowLogs(selectedFlowId, 1, logsFilter);
        } catch (error) {
            console.error('Error deleting campaign logs:', error);
            toast.error('Failed to delete logs');
        } finally {
            setDeletingLogs(false);
        }
    };

    // Delete flow
    const handleDeleteFlow = async (id) => {
        if (!window.confirm('Are you sure you want to delete this campaign?')) return;

        try {
            setDeletingCampaigns(prev => new Set([...prev, id]));
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            await apiClient.delete(`/api/email-campaigns/${id}`, { headers });
            queryClient.invalidateQueries(['emailCampaigns', currentWorkspace?.id]);
            toast.success('Campaign deleted successfully');
        } catch (error) {
            console.error('Error deleting campaign:', error);
            toast.error('Failed to delete campaign');
        } finally {
            setDeletingCampaigns(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };

    // Toggle flow status
    const handleToggleFlow = async (flow) => {
        try {
            setTogglingCampaigns(prev => new Set([...prev, flow.id]));
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const newStatus = flow.status === 'active' ? 'paused' : 'active';

            await apiClient.put(
                `/api/email-campaigns/${flow.id}/status`,
                { status: newStatus },
                { headers }
            );

            queryClient.invalidateQueries(['emailCampaigns', currentWorkspace?.id]);
            toast.success(`Campaign ${newStatus === 'active' ? 'activated' : 'paused'} successfully`);
        } catch (error) {
            console.error('Error toggling campaign status:', error);
            toast.error('Failed to update campaign status');
        } finally {
            setTogglingCampaigns(prev => {
                const next = new Set(prev);
                next.delete(flow.id);
                return next;
            });
        }
    };

    // Handle flow selection
    const handleFlowSelect = (flowId) => {
        setSelectedFlows(prev =>
            prev.includes(flowId)
                ? prev.filter(id => id !== flowId)
                : [...prev, flowId]
        );
    };

    // Handle select all
    const handleSelectAll = () => {
        if (selectedFlows.length === flows.length) {
            setSelectedFlows([]);
        } else {
            setSelectedFlows(flows.map(f => f.id));
        }
    };

    // Pause all active campaigns and stop cron scheduling
    const handleStopAll = async () => {
        if (!window.confirm('Stop all active campaigns in this workspace? All scheduled cron jobs will be paused.')) {
            return;
        }

        try {
            setStoppingAll(true);
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const response = await apiClient.post('/api/email-campaigns/stop-all', {}, { headers });

            queryClient.invalidateQueries(['emailCampaigns', currentWorkspace?.id]);
            setSelectedFlows([]);

            const pausedCount = response.data.pausedCount || 0;
            if (pausedCount === 0) {
                toast.success('No active campaigns were running');
            } else {
                toast.success(`${pausedCount} campaign(s) stopped. All cron schedules paused.`);
            }
        } catch (error) {
            console.error('Error stopping all campaigns:', error);
            toast.error('Failed to stop all campaigns');
        } finally {
            setStoppingAll(false);
        }
    };

    // Pause selected active campaigns
    const handleBulkStop = async () => {
        const activeSelected = selectedFlows.filter((id) => {
            const flow = flows.find((item) => item.id === id);
            return flow?.status === 'active';
        });

        if (activeSelected.length === 0) {
            toast.error('No active campaigns selected to stop');
            return;
        }

        if (!window.confirm(`Stop ${activeSelected.length} selected campaign(s)? Their cron schedules will be paused.`)) {
            return;
        }

        try {
            setBulkStopping(true);
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const response = await apiClient.post(
                '/api/email-campaigns/pause-selected',
                { ids: activeSelected },
                { headers }
            );

            queryClient.invalidateQueries(['emailCampaigns', currentWorkspace?.id]);
            setSelectedFlows([]);
            toast.success(`${response.data.pausedCount || activeSelected.length} campaign(s) stopped`);
        } catch (error) {
            console.error('Error stopping selected campaigns:', error);
            toast.error('Failed to stop selected campaigns');
        } finally {
            setBulkStopping(false);
        }
    };

    // Handle bulk delete
    const handleBulkDelete = async () => {
        if (!window.confirm(`Are you sure you want to delete ${selectedFlows.length} campaigns?`)) {
            return;
        }

        try {
            setBulkDeleting(true);
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

            await Promise.all(
                selectedFlows.map(id => apiClient.delete(`/api/email-campaigns/${id}`, { headers }))
            );

            queryClient.invalidateQueries(['emailCampaigns', currentWorkspace?.id]);
            setSelectedFlows([]);
            toast.success(`${selectedFlows.length} campaigns deleted successfully`);
        } catch (error) {
            console.error('Error bulk deleting campaigns:', error);
            toast.error('Failed to delete campaigns');
        } finally {
            setBulkDeleting(false);
        }
    };

    // Handle search
    const handleSearch = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    // Handle status filter
    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        setCurrentPage(1);
    };

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status || 'draft') {
            case 'active': return 'bg-green-100 text-green-800';
            case 'paused': return 'bg-yellow-100 text-yellow-800';
            case 'draft': return 'bg-gray-100 text-gray-800';
            case 'completed': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Get trigger icon
    const getTriggerIcon = (trigger) => {
        switch (trigger) {
            case 'scheduled': return <Calendar className="h-4 w-4" />;
            default: return <Calendar className="h-4 w-4" />;
        }
    };

    return (
        <div>
            {/* Unified Campaigns Dashboard */}
            <div className="bg-white rounded-xl shadow-sm border">
                {/* Header Section */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Email Campaigns</h1>
                            <p className="text-gray-600 mt-2">Create automated email sequences for your contacts</p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={handleStopAll}
                                disabled={stoppingAll}
                                className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {stoppingAll ? <Loader2 className="h-5 w-5 animate-spin" /> : <Pause className="h-5 w-5" />}
                                <span>{stoppingAll ? 'Stopping...' : 'Stop All'}</span>
                            </button>
                            <button
                                onClick={() => navigate('/smtp-management')}
                                className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                <Mail className="h-5 w-5" />
                                <span>Manage SMTP</span>
                            </button>
                            <button
                                onClick={() => navigate('/create-email-campaign')}
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Plus className="h-5 w-5" />
                                <span>Create Campaign</span>
                            </button>
                        </div>
                    </div>

                    {/* Stats Cards - Inline */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Total Campaigns</p>
                                    <p className="text-xl font-bold text-gray-900">{flows.length}</p>
                                </div>
                                <BarChart3 className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
                                    <p className="text-xl font-bold text-green-600">
                                        {flows.filter(f => f.status === 'active').length}
                                    </p>
                                </div>
                                <Play className="h-6 w-6 text-green-600" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Draft Campaigns</p>
                                    <p className="text-xl font-bold text-gray-600">
                                        {flows.filter(f => f.status === 'draft').length}
                                    </p>
                                </div>
                                <Edit className="h-6 w-6 text-gray-600" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Completed</p>
                                    <p className="text-xl font-bold text-blue-600">
                                        {flows.filter(f => f.status === 'completed').length}
                                    </p>
                                </div>
                                <Calendar className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                    </div>

                    {/* Search and Filter Bar */}
                    <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="flex flex-col sm:flex-row gap-4 flex-1">
                            {/* Search */}
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search campaigns..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Status Filter */}
                            <div className="relative">
                                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <select
                                    value={statusFilter}
                                    onChange={(e) => handleStatusFilter(e.target.value)}
                                    className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                                >
                                    <option value="">All Status</option>
                                    <option value="draft">Draft</option>
                                    <option value="active">Active</option>
                                    <option value="completed">Completed</option>
                                    <option value="paused">Paused</option>
                                </select>
                            </div>
                        </div>

                        {/* Bulk Actions */}
                        {selectedFlows.length > 0 && (
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">
                                    {selectedFlows.length} selected
                                </span>
                                <button
                                    onClick={handleBulkStop}
                                    disabled={bulkStopping}
                                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {bulkStopping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pause className="h-4 w-4" />}
                                    {bulkStopping ? 'Stopping...' : 'Stop Selected'}
                                </button>
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={bulkDeleting}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                    {bulkDeleting ? 'Deleting...' : 'Delete Selected'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Campaigns Table Section */}
                <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Campaigns</h2>

                    {loading ? (
                        <EmailCampaignsSkeleton />
                    ) : flows.length === 0 ? (
                        <div className="p-8 text-center">
                            <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns found</h3>
                            <p className="text-gray-600 mb-4">
                                {searchTerm || statusFilter
                                    ? 'Try adjusting your search or filter criteria'
                                    : 'Create your first email campaign to get started'
                                }
                            </p>
                            {!searchTerm && !statusFilter && (
                                <button
                                    onClick={() => navigate('/create-email-campaign')}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Create Your First Campaign
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Table */}
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left">
                                                <button
                                                    onClick={handleSelectAll}
                                                    className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                                                >
                                                    {selectedFlows.length === flows.length ? (
                                                        <CheckSquare className="h-4 w-4" />
                                                    ) : (
                                                        <Square className="h-4 w-4" />
                                                    )}
                                                    Select All
                                                </button>
                                            </th>
                                            <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                                            <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Status</th>
                                            <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Trigger</th>
                                            <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Schedule</th>
                                            <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Created</th>
                                            <th className="px-6 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {flows.map((flow) => (
                                            <tr key={flow.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4">
                                                    <button
                                                        onClick={() => handleFlowSelect(flow.id)}
                                                        className="flex items-center gap-2"
                                                    >
                                                        {selectedFlows.includes(flow.id) ? (
                                                            <CheckSquare className="h-4 w-4 text-blue-600" />
                                                        ) : (
                                                            <Square className="h-4 w-4 text-gray-400" />
                                                        )}
                                                    </button>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {flow.name || 'Unnamed Campaign'}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {flow.description || 'No description'}
                                                        </div>
                                                        {flow.email_stats && flow.email_stats.total_emails > 0 && (
                                                            <div className="mt-1.5 flex items-center gap-2">
                                                                <div className="flex items-center gap-1">
                                                                    <Mail className="h-3 w-3 text-green-600" />
                                                                    <span className="text-xs font-medium text-green-600">
                                                                        {flow.email_stats.emails_sent}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500">sent today</span>
                                                                </div>
                                                                {flow.email_stats.emails_failed > 0 && (
                                                                    <>
                                                                        <span className="text-xs text-gray-300">/</span>
                                                                        <span className="text-xs text-red-600 font-medium">
                                                                            {flow.email_stats.emails_failed} failed
                                                                        </span>
                                                                    </>
                                                                )}
                                                                <span className="text-xs text-gray-400">
                                                                    of {flow.email_stats.total_emails}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {campaignProgress[flow.id]?.status === 'running' ? (
                                                        <div className="space-y-2 min-w-[160px]">
                                                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                                                Sending...
                                                            </span>
                                                            <div className="text-xs text-gray-600">
                                                                {campaignProgress[flow.id].sent + campaignProgress[flow.id].failed + campaignProgress[flow.id].skipped}
                                                                /{campaignProgress[flow.id].total}
                                                            </div>
                                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                                <div
                                                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                                    style={{
                                                                        width: `${Math.min(
                                                                            100,
                                                                            Math.round(
                                                                                ((campaignProgress[flow.id].sent + campaignProgress[flow.id].failed + campaignProgress[flow.id].skipped)
                                                                                    / Math.max(campaignProgress[flow.id].total, 1)) * 100
                                                                            )
                                                                        )}%`,
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="text-xs">
                                                                <span className="text-green-600">{campaignProgress[flow.id].sent} sent</span>
                                                                {campaignProgress[flow.id].failed > 0 && (
                                                                    <span className="text-red-600 ml-2">{campaignProgress[flow.id].failed} failed</span>
                                                                )}
                                                                {campaignProgress[flow.id].skipped > 0 && (
                                                                    <span className="text-yellow-600 ml-2">{campaignProgress[flow.id].skipped} skipped</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(flow.status)}`}>
                                                            {flow.status || 'draft'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-1 text-sm text-gray-600">
                                                        {getTriggerIcon(flow.trigger_type || 'scheduled')}
                                                        <span className="capitalize">{(flow.trigger_type || 'scheduled').replace('_', ' ')}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-600">
                                                        {flow.trigger_type === 'scheduled' && flow.schedule_config ? (
                                                            <div>
                                                                {(() => {
                                                                    const scheduleConfig = typeof flow.schedule_config === 'string'
                                                                        ? JSON.parse(flow.schedule_config)
                                                                        : flow.schedule_config;
                                                                    return scheduleConfig.schedule_type === 'once' ? (
                                                                        <div>
                                                                            <div>Once: {formatIndiaTime(scheduleConfig.schedule_time, 'relative')}</div>
                                                                            <div className="text-xs text-gray-500">
                                                                                {formatIndiaTime(scheduleConfig.schedule_time, 'time-ampm')}
                                                                            </div>
                                                                            {flow.status === 'active' && isPastIndiaTime(scheduleConfig.schedule_time) && (
                                                                                <div className="flex items-center gap-1 mt-1">
                                                                                    <Clock className="h-3 w-3 text-gray-400" />
                                                                                    <span className="text-gray-500 text-xs">Scheduled time has passed</span>
                                                                                </div>
                                                                            )}
                                                                            {flow.status === 'active' && countdowns[flow.id] && (
                                                                                <div className="flex items-center gap-1 mt-1 group relative">
                                                                                    <Clock className={`h-4 w-4 ${countdowns[flow.id].days === 0 && countdowns[flow.id].hours === 0 && countdowns[flow.id].minutes < 5
                                                                                        ? 'text-red-500 animate-pulse'
                                                                                        : 'text-orange-500'
                                                                                        }`} />
                                                                                    <span className={`font-bold text-sm ${countdowns[flow.id].days === 0 && countdowns[flow.id].hours === 0 && countdowns[flow.id].minutes < 5
                                                                                        ? 'text-red-600'
                                                                                        : 'text-orange-600'
                                                                                        }`}>
                                                                                        {countdowns[flow.id].days > 0 && `${countdowns[flow.id].days}d `}
                                                                                        {countdowns[flow.id].hours > 0 && `${countdowns[flow.id].hours}h `}
                                                                                        {countdowns[flow.id].minutes > 0 && `${countdowns[flow.id].minutes}m `}
                                                                                        {countdowns[flow.id].seconds}s
                                                                                    </span>
                                                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                                                        Next run: {formatIndiaTime(countdowns[flow.id].nextExecution, 'time-ampm')}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div>
                                                                            <div>Cron: {scheduleConfig.cron_expr}</div>
                                                                            {flow.status === 'active' && countdowns[flow.id] && (
                                                                                <div className="flex items-center gap-1 mt-1 group relative">
                                                                                    <Clock className={`h-4 w-4 ${countdowns[flow.id].days === 0 && countdowns[flow.id].hours === 0 && countdowns[flow.id].minutes < 5
                                                                                        ? 'text-red-500 animate-pulse'
                                                                                        : 'text-orange-500'
                                                                                        }`} />
                                                                                    <span className={`font-bold text-sm ${countdowns[flow.id].days === 0 && countdowns[flow.id].hours === 0 && countdowns[flow.id].minutes < 5
                                                                                        ? 'text-red-600'
                                                                                        : 'text-orange-600'
                                                                                        }`}>
                                                                                        {countdowns[flow.id].days > 0 && `${countdowns[flow.id].days}d `}
                                                                                        {countdowns[flow.id].hours > 0 && `${countdowns[flow.id].hours}h `}
                                                                                        {countdowns[flow.id].minutes > 0 && `${countdowns[flow.id].minutes}m `}
                                                                                        {countdowns[flow.id].seconds}s
                                                                                    </span>
                                                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                                                        Next run: {formatIndiaTime(countdowns[flow.id].nextExecution, 'time-ampm')}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400">-</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-600">
                                                    {flow.created_at ? formatIndiaTime(flow.created_at, 'relative') : 'Unknown'}
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => navigate(`/edit-email-campaign/${flow.id}`)}
                                                            className="p-3 text-gray-400 hover:text-blue-600 transition-colors"
                                                            title="Edit Campaign"
                                                        >
                                                            <Edit className="h-5 w-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleToggleFlow(flow)}
                                                            disabled={togglingCampaigns.has(flow.id)}
                                                            className={`p-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${togglingCampaigns.has(flow.id)
                                                                ? 'text-gray-400'
                                                                : flow.status === 'active'
                                                                    ? 'text-yellow-600 hover:text-yellow-700'
                                                                    : 'text-green-600 hover:text-green-700'
                                                                }`}
                                                            title={
                                                                togglingCampaigns.has(flow.id)
                                                                    ? 'Updating...'
                                                                    : flow.status === 'active'
                                                                        ? 'Pause Campaign'
                                                                        : 'Activate Campaign'
                                                            }
                                                        >
                                                            {togglingCampaigns.has(flow.id) ? (
                                                                <Loader2 className="h-5 w-5 animate-spin" />
                                                            ) : flow.status === 'active' ? (
                                                                <Pause className="h-5 w-5" />
                                                            ) : (
                                                                <Play className="h-5 w-5" />
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => fetchFlowLogs(flow.id)}
                                                            disabled={logsLoading && selectedFlowId === flow.id}
                                                            className="p-3 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            title={logsLoading && selectedFlowId === flow.id ? 'Loading logs...' : 'View Campaign Logs'}
                                                        >
                                                            {logsLoading && selectedFlowId === flow.id ? (
                                                                <Loader2 className="h-5 w-5 animate-spin" />
                                                            ) : (
                                                                <BarChart3 className="h-5 w-5" />
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteFlow(flow.id)}
                                                            disabled={deletingCampaigns.has(flow.id)}
                                                            className={`p-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${deletingCampaigns.has(flow.id)
                                                                ? 'text-gray-400'
                                                                : 'text-gray-400 hover:text-red-600'
                                                                }`}
                                                            title={deletingCampaigns.has(flow.id) ? 'Deleting...' : 'Delete Campaign'}
                                                        >
                                                            {deletingCampaigns.has(flow.id) ? (
                                                                <Loader2 className="h-5 w-5 animate-spin" />
                                                            ) : (
                                                                <Trash2 className="h-5 w-5" />
                                                            )}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {pagination.pages > 1 && (
                                <div className="px-6 py-4 border-t bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-gray-700">
                                            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} campaigns
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => handlePageChange(pagination.page - 1)}
                                                disabled={pagination.page === 1}
                                                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <ChevronLeft className="h-4 w-4" />
                                            </button>

                                            <div className="flex items-center gap-1">
                                                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                                                    const pageNum = i + 1;
                                                    return (
                                                        <button
                                                            key={pageNum}
                                                            onClick={() => handlePageChange(pageNum)}
                                                            className={`px-3 py-1 text-sm rounded ${pagination.page === pageNum
                                                                ? 'bg-blue-600 text-white'
                                                                : 'text-gray-600 hover:bg-gray-200'
                                                                }`}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <button
                                                onClick={() => handlePageChange(pagination.page + 1)}
                                                disabled={pagination.page === pagination.pages}
                                                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <ChevronRight className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Campaign Logs Modal */}
            {showFlowLogs && flowLogs && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b">
                            <h3 className="text-lg font-medium text-gray-900">
                                📊 Campaign Logs: {flowLogs.flow.name}
                            </h3>
                            <button
                                onClick={() => setShowFlowLogs(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                            {/* Summary */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <p className="text-sm font-medium text-blue-600">Total Executions</p>
                                    <p className="text-2xl font-bold text-blue-900">{flowLogs.summary.total_executions || 0}</p>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <p className="text-sm font-medium text-green-600">Emails Sent</p>
                                    <p className="text-2xl font-bold text-green-900">{flowLogs.summary.emails_sent || 0}</p>
                                </div>
                                <div className="bg-red-50 p-4 rounded-lg">
                                    <p className="text-sm font-medium text-red-600">Emails Failed</p>
                                    <p className="text-2xl font-bold text-red-900">{flowLogs.summary.emails_failed || 0}</p>
                                </div>
                                <div className="bg-yellow-50 p-4 rounded-lg">
                                    <p className="text-sm font-medium text-yellow-600">Errors</p>
                                    <p className="text-2xl font-bold text-yellow-900">{flowLogs.summary.errors || 0}</p>
                                </div>
                            </div>

                            {/* Logs */}
                            <div className="space-y-3">
                                <div className="mb-3">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-md font-medium text-gray-900">Execution Logs</h4>
                                        <div className="flex items-center space-x-3">
                                            <button
                                                onClick={deleteAllFlowLogs}
                                                disabled={deletingLogs || flowLogs.logs.length === 0}
                                                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                            >
                                                {deletingLogs ? <Loader2 className="h-3 w-3 animate-spin" /> : <span>🗑️</span>}
                                                {deletingLogs ? 'Deleting...' : 'Delete All'}
                                            </button>
                                            <span className="text-sm text-gray-500">
                                                Page {flowLogs.pagination.page} of {flowLogs.pagination.pages}
                                                ({flowLogs.pagination.total} total)
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Filter Buttons */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleLogsFilterChange('all')}
                                            className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                                logsFilter === 'all'
                                                    ? 'bg-gray-200 text-gray-900 font-medium'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            All
                                        </button>
                                        <button
                                            onClick={() => handleLogsFilterChange('success')}
                                            className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                                logsFilter === 'success'
                                                    ? 'bg-green-200 text-green-900 font-medium'
                                                    : 'bg-green-50 text-green-600 hover:bg-green-100'
                                            }`}
                                        >
                                            Success
                                        </button>
                                        <button
                                            onClick={() => handleLogsFilterChange('error')}
                                            className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                                logsFilter === 'error'
                                                    ? 'bg-red-200 text-red-900 font-medium'
                                                    : 'bg-red-50 text-red-600 hover:bg-red-100'
                                            }`}
                                        >
                                            Failed
                                        </button>
                                        <button
                                            onClick={() => handleLogsFilterChange('warning')}
                                            className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                                logsFilter === 'warning'
                                                    ? 'bg-yellow-200 text-yellow-900 font-medium'
                                                    : 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                                            }`}
                                        >
                                            Warning
                                        </button>
                                    </div>
                                </div>

                                {flowLogs.logs.length === 0 ? (
                                    <p className="text-gray-500 text-center py-8">No logs found for this campaign</p>
                                ) : (
                                    <>
                                        {flowLogs.logs.map((log) => (
                                            <div key={log.id} className={`p-4 rounded-lg border-l-4 ${log.logLevel === 'error' ? 'bg-red-50 border-red-400' :
                                                log.logLevel === 'warning' ? 'bg-yellow-50 border-yellow-400' :
                                                    log.logLevel === 'success' ? 'bg-green-50 border-green-400' :
                                                        'bg-gray-50 border-gray-400'
                                                }`}>
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center space-x-2 mb-1">
                                                            <span className={`px-2 py-1 text-xs rounded-full ${log.logLevel === 'error' ? 'bg-red-100 text-red-800' :
                                                                log.logLevel === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                                                    log.logLevel === 'success' ? 'bg-green-100 text-green-800' :
                                                                        'bg-gray-100 text-gray-800'
                                                                }`}>
                                                                {log.logLevel}
                                                            </span>
                                                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                                                                {log.logType}
                                                            </span>
                                                            {log.contactName && (
                                                                <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                                                                    {log.contactName}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-gray-900 mb-1">{log.message}</p>
                                                        {log.details && (
                                                            <details className="text-xs text-gray-600">
                                                                <summary className="cursor-pointer hover:text-gray-800">Details</summary>
                                                                <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                                                                    {JSON.stringify(log.details, null, 2)}
                                                                </pre>
                                                            </details>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center space-x-2 ml-4">
                                                        <div className="text-xs text-gray-500">
                                                            <p>{log.createdAtIST}</p>
                                                            {log.executionId && (
                                                                <p>Exec: {log.executionId}</p>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => deleteLog(log.id)}
                                                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                                            title="Delete this log"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Pagination Controls */}
                                        {flowLogs.pagination.pages > 1 && (
                                            <div className="flex items-center justify-between mt-6 pt-4 border-t">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => fetchFlowLogs(selectedFlowId, logsPage - 1, logsFilter)}
                                                        disabled={logsPage === 1}
                                                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                                                    >
                                                        Previous
                                                    </button>
                                                    <span className="text-sm text-gray-600">
                                                        Page {logsPage} of {flowLogs.pagination.pages}
                                                    </span>
                                                    <button
                                                        onClick={() => fetchFlowLogs(selectedFlowId, logsPage + 1, logsFilter)}
                                                        disabled={logsPage === flowLogs.pagination.pages}
                                                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-sm text-gray-600">Show:</span>
                                                    <select
                                                        value={logsPerPage}
                                                        onChange={(e) => {
                                                            setLogsPerPage(parseInt(e.target.value));
                                                            fetchFlowLogs(selectedFlowId, 1, logsFilter);
                                                        }}
                                                        className="px-2 py-1 text-sm border rounded"
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={20}>20</option>
                                                        <option value={50}>50</option>
                                                        <option value={100}>100</option>
                                                    </select>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default EmailCampaigns;
